import {Component, computed, effect, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {InputText} from 'primeng/inputtext';
import {BaseStepComponent} from '../../../../../shared/components/wizard/base-step.component';
import {CompanyWizardService} from '../company-wizard.service';
import {CreateUpdateCompanyRequest} from '@frontend/shared-api';
import {CountryService} from '@frontend/shared';
import {Panel} from 'primeng/panel';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CityStepData, Office} from '../company-wizard.types';
import {Message} from 'primeng/message';
import {TranslocoDirective} from '@jsverse/transloco';
import {v7 as uuid} from 'uuid';
import {validateEntities} from '../../../../../shared/utils/array-utils';
import {Tooltip} from 'primeng/tooltip';

interface CityUpdate {
  countryCode: string;
  index: number;
  value: string;
}

interface City {
  uuid: string;
  name: string;
  offices?: Office[];
}

interface Country {
  countryCode: string;
  cities: City[];
}

@Component({
  selector: 'app-company-wizard-city-step',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputText,
    Panel,
    Message,
    TranslocoDirective,
    Tooltip,
  ],
  templateUrl: './company-wizard-city-step.component.html',
  styleUrls: ['./company-wizard-city-step.component.scss'],
})
export class CompanyWizardCityStepComponent
  extends BaseStepComponent<CreateUpdateCompanyRequest>
  implements OnInit
{
  override readonly stepIndex = 3;

  private readonly fb = inject(FormBuilder);
  private readonly companyWizardService = inject(CompanyWizardService);
  protected readonly countryService = inject(CountryService);

  private readonly isEditing = signal(false);
  protected readonly touchedFields = signal<{[countryCode: string]: boolean[]}>({});

  private readonly cityUpdateSubject = new Subject<CityUpdate>();

  override readonly form = this.fb.group({
    cities: this.fb.control<{[countryCode: string]: City[]}>({}, [Validators.required]),
  });

  countries = signal<Country[]>([]);
  cities = signal<{[countryCode: string]: City[]}>({});

  private readonly hasCities = computed(() => Object.keys(this.cities()).length > 0);

  private readonly allCitiesAreValid = computed(() => {
    return Object.entries(this.cities()).every(([, countryCities]) =>
      validateEntities(countryCities, 'name'),
    );
  });

  private readonly allCitiesOfficesAreValid = computed((): boolean => {
    return Object.values(this.cities()).every((citiesArray: City[]) =>
      citiesArray.every((city: City) => {
        if (!city.offices || city.offices.length === 0) {
          return false;
        }
        return validateEntities(city.offices, 'name') && validateEntities(city.offices, 'address');
      }),
    );
  });

  readonly isValidComputed = computed(
    () => this.hasCities() && this.allCitiesAreValid() && !this.isEditing(),
  );

  constructor() {
    super();
    this.setupCityUpdates();

    effect(() => {
      const officesValid = this.allCitiesOfficesAreValid();
      this.companyWizardService.updateStepValidity({
        stepIndex: 4,
        isValid: officesValid,
      });
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initializeCitiesData();
  }

  addCity(countryCode: string): void {
    if (!countryCode) {
      console.error('Cannot add city: country code is required');
      return;
    }

    try {
      this.addCityToCountry(countryCode);
      this.syncCountryWithCities(countryCode);
      this.updateForm();
    } catch (error) {
      console.error(`Failed to add city to country ${countryCode}:`, error);
    }
  }

  removeCity(countryCode: string, index: number): void {
    if (!countryCode || index < 0) {
      console.error('Cannot remove city: invalid country code or index');
      return;
    }

    try {
      const citiesMap = this.cities();
      const countryCities = citiesMap[countryCode];

      if (!countryCities || countryCities.length <= 1) {
        console.warn(`Cannot remove the only city for ${countryCode}`);
        return;
      }

      this.removeCityFromCountry(countryCode, index);
      this.removeTouchedFieldForCity(countryCode, index);
      this.syncCountryWithCities(countryCode);
      this.updateForm();
    } catch (error) {
      console.error(`Failed to remove city from country ${countryCode} at index ${index}:`, error);
    }
  }

  shouldShowCityError(countryCode: string, index: number): boolean {
    const isTouched = this.touchedFields()[countryCode]?.[index] || false;
    const city = this.cities()[countryCode]?.[index];
    const isEmpty = !city?.name || city.name.trim().length === 0;
    const isDuplicate = this.isCityDuplicate(countryCode, index, city?.name || '');

    return isTouched && (isEmpty || isDuplicate);
  }

  onCityBlur(countryCode: string, index: number, currentValue: string): void {
    this.handleCityUpdate({countryCode, index, value: currentValue});
    const countryTouched: boolean[] = this.touchedFields()[countryCode] || [];
    const updatedTouched = [...countryTouched];
    updatedTouched[index] = true;
    this.touchedFields.update((touched) => ({
      ...touched,
      [countryCode]: updatedTouched,
    }));
  }

  onCityNameChange(countryCode: string, index: number, value: string): void {
    if (!this.isEditing()) {
      this.isEditing.set(true);
    }
    this.cityUpdateSubject.next({
      countryCode,
      index,
      value,
    });
  }

  isCityDuplicate(countryCode: string, index: number, cityName: string): boolean {
    if (!cityName.trim()) return false;

    const countryCities = this.cities()[countryCode] || [];
    return countryCities.some(
      (city, i) => i !== index && city.name.trim().toLowerCase() === cityName.trim().toLowerCase(),
    );
  }

  private initializeCitiesData(): void {
    if (!this.formData?.countries || !this.isValidCompanyRequest(this.formData)) {
      return;
    }

    const stepData = this.companyWizardService.getStepData<CityStepData>(this.stepIndex);
    const citiesData: {[countryCode: string]: City[]} = {};
    const selectedCountries = this.formData.countries;
    const mappedCountries = selectedCountries.map((country) => ({
      ...country,
      cities: (country.cities || []).map((city) => ({
        uuid: uuid(),
        name: city.name,
      })),
    }));

    this.countries.set(mappedCountries);

    mappedCountries.forEach((country) => {
      const savedCities = country.cities || [];
      citiesData[country.countryCode] = savedCities.length
        ? savedCities
        : [{uuid: uuid(), name: ''}];
    });

    if (stepData?.cities) {
      const selectedCountryCodes = selectedCountries.map((country) => country.countryCode);
      Object.keys(stepData.cities)
        .filter((countryCode) => selectedCountryCodes.includes(countryCode))
        .forEach((countryCode) => {
          citiesData[countryCode] = stepData.cities[countryCode];
        });
    }

    this.cities.set(citiesData);

    this.updateForm();
  }

  private setupCityUpdates(): void {
    this.cityUpdateSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(
          (prev, curr) =>
            prev.countryCode === curr.countryCode &&
            prev.index === curr.index &&
            prev.value === curr.value,
        ),
        takeUntilDestroyed(),
      )
      .subscribe((update) => {
        this.handleCityUpdate(update);
        this.isEditing.set(false);
      });
  }

  private handleCityUpdate(update: CityUpdate): void {
    const currentCities = {...this.cities()};
    if (!currentCities[update.countryCode]) {
      currentCities[update.countryCode] = [{uuid: uuid(), name: ''}];
    }
    currentCities[update.countryCode][update.index].name = update.value;
    this.cities.set(currentCities);
    this.updateForm();
  }

  private isValidCompanyRequest(
    data: Partial<CreateUpdateCompanyRequest>,
  ): data is CreateUpdateCompanyRequest {
    return !!data.company && Array.isArray(data.countries);
  }

  private updateForm(): void {
    this.form.patchValue({cities: this.cities()}, {emitEvent: false});

    this.companyWizardService.updateStepData({
      stepIndex: this.stepIndex,
      data: {cities: this.cities()},
    });
  }

  private addCityToCountry(countryCode: string): void {
    const citiesMap = {...this.cities()};

    if (!citiesMap[countryCode] || !Array.isArray(citiesMap[countryCode])) {
      citiesMap[countryCode] = [];
    }

    citiesMap[countryCode].push({uuid: uuid(), name: ''});
    this.cities.set(citiesMap);
  }

  private removeCityFromCountry(countryCode: string, index: number): void {
    const citiesMap = {...this.cities()};
    citiesMap[countryCode] = citiesMap[countryCode].filter((_, i) => i !== index);
    this.cities.set(citiesMap);
  }

  private syncCountryWithCities(countryCode: string): void {
    const countriesList = [...this.countries()];
    const citiesMap = this.cities();
    const countryIndex = countriesList.findIndex((country) => country.countryCode === countryCode);

    if (countryIndex !== -1 && citiesMap[countryCode]) {
      countriesList[countryIndex] = {
        ...countriesList[countryIndex],
        cities: [...citiesMap[countryCode]],
      };
      this.countries.set(countriesList);
    }
  }

  private removeTouchedFieldForCity(countryCode: string, removedIndex: number): void {
    this.touchedFields.update((prev) => {
      const countryTouched = prev[countryCode] ? [...prev[countryCode]] : [];
      countryTouched.splice(removedIndex, 1);
      return {
        ...prev,
        [countryCode]: countryTouched,
      };
    });
  }

  isCityDeletable(countryCode: string, index: number): boolean {
    if (!this.companyWizardService.isEdit()) {
      return true;
    }
    const currentCity = this.cities()[countryCode]?.[index];
    if (!currentCity) {
      return true;
    }
    const stepData = this.companyWizardService.getStepData<CityStepData>(this.stepIndex);
    if (!stepData?.cities?.[countryCode]) {
      return true;
    }

    const cityInStepData = stepData.cities[countryCode].find((c) => c.uuid === currentCity.uuid);

    return cityInStepData?.isDeletable !== false;
  }
}
